/**
 * Filmop USA Branded Store Page
 * Amazon-style branded storefront within the Hospital Marketplace
 * Features catalogs, products, and Filmop branding
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  ShoppingCart,
  Plus,
  Minus,
  Star,
  ChevronRight,
  FileText,
  ExternalLink,
  Package,
  Download,
  Truck,
  CheckCircle,
  Building2,
  Award,
  BookOpen,
  Grid3X3,
  List,
  Filter,
  X,
  RefreshCw,
  Phone,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import HospitalLayout from "@/components/layouts/HospitalLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Filmop brand colors
const FILMOP_BLUE = "#003366";
const FILMOP_GOLD = "#c9a227";

// Filmop Catalogs from their website
const FILMOP_CATALOGS = [
  {
    id: "acute-care",
    name: "Acute Care",
    description: "Hospital & acute care cleaning solutions",
    image: "https://www.filmopusa.com/images/literature/cover-healthcare-full-catalog.gif",
    url: "https://www.filmopusa.com/Filmop-Healthcare-Brochure-Interactive.html",
    industries: ["Hospitals", "Emergency Rooms", "ICU", "Operating Rooms"]
  },
  {
    id: "education",
    name: "Education",
    description: "K-12 & university cleaning equipment",
    image: "https://www.filmopusa.com/images/literature/cover-educational.gif",
    url: "https://www.filmopusa.com/Filmop-Education-Brochure-Interactive.html",
    industries: ["Schools", "Universities", "Daycare Centers", "Libraries"]
  },
  {
    id: "facilities",
    name: "Facilities",
    description: "General facility maintenance solutions",
    image: "https://www.filmopusa.com/images/literature/cover-facility-brochure.gif",
    url: "https://www.filmopusa.com/Filmop-Facility-Brochure-Interactive.html",
    industries: ["Office Buildings", "Government", "Industrial", "Retail"]
  },
  {
    id: "skilled-care",
    name: "Skilled Care",
    description: "Long-term care & nursing facilities",
    image: "https://www.filmopusa.com/images/literature/cover-skilled-care.gif",
    url: "https://www.filmopusa.com/Filmop-Skilled-Care-Brochure-Interactive.html",
    industries: ["Nursing Homes", "Assisted Living", "Rehab Centers"]
  },
  {
    id: "microfiber",
    name: "Microfiber",
    description: "Premium microfiber mops & cleaning cloths",
    image: "https://www.filmopusa.com/images/literature/cover-microfiber-brochure.gif",
    url: "https://www.filmopusa.com/Filmop-Microfiber-Brochure-Interactive.html",
    industries: ["All Industries", "Healthcare", "Hospitality"]
  },
  {
    id: "hospitality",
    name: "Hospitality",
    description: "Hotel & hospitality cleaning solutions",
    image: "https://www.filmopusa.com/images/literature/cover-hospitality.gif",
    url: "https://www.filmopusa.com/Filmop-Hospitality-Brochure-Interactive.html",
    industries: ["Hotels", "Resorts", "Casinos", "Restaurants"]
  }
];

// Product subcategories - each has a `matches(product)` predicate that resolves
// against the real DB subcategory values ("Omega Carts", "Alpha Workstations",
// "AB Plus Workstations", "Tools, Textiles, Buckets & Bins") + description keywords
// so filter chips correctly return products.
const PRODUCT_CATEGORIES = [
  {
    id: "all",
    name: "All Products",
    matches: () => true,
  },
  {
    id: "workstations",
    name: "Workstations & Carts",
    matches: (p) =>
      ["Omega Carts", "Alpha Workstations", "AB Plus Workstations"].includes(p.subcategory),
  },
  {
    id: "microfiber_mops",
    name: "Microfiber Mops",
    matches: (p) =>
      /(MOP|VELCRO|POCKET SYSTEM|ERGOSWING|SPIN-N-DROP|UNI-TAB|V-SWEEPER|PRESS-CONNECT|STRING MOP|TUBE MOP|DUST MOP|TOTAL MOP|DROP.*BUCKETLESS|MICROFIBER)/i.test(
        p.description || ""
      ) && !/BUCKET|SQUEEGEE|CART/i.test(p.description || ""),
  },
  {
    id: "charging_systems",
    name: "Charging Systems",
    matches: (p) => /(TOP-DOWN|TOP DOWN|CHARGING BUCKET|EQUODOSE)/i.test(p.description || ""),
  },
  {
    id: "wringing_systems",
    name: "Wringing Systems",
    matches: (p) =>
      /(WRINGER|PRESS MOP WRING|TWICE.*DUAL BUCKET|ONEFRED|ONE FRED|ONE-FRED|SIDE PRESS|DOWN PRESS|BOOST|PUNTO)/i.test(
        p.description || ""
      ),
  },
  {
    id: "surface_cleaning",
    name: "Surface Cleaning",
    matches: (p) =>
      /(BRILLIANT|BRILLANTE|RAPIDO|HAND TROWEL|GLASS CLEANING|POCKET TROWEL)/i.test(
        p.description || ""
      ),
  },
  {
    id: "tools",
    name: "Tools & Accessories",
    matches: (p) =>
      /(HANDLE|SQUEEGEE|CLOTH|SLEEVE|BUCKET|JOBBY|BAGGY|DELTA|POLARIS|SNAKE|SPILLO|QUIK-STIK|MONO-WIPE|WIPE DISPENSER|DUSTER|WHEEL|HOLDER)/i.test(
        p.description || ""
      ),
  },
];

// Catalog Card Component
const CatalogCard = ({ catalog, onView }) => (
  <div 
    data-testid={`catalog-${catalog.id}`}
    className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
    onClick={onView}
  >
    <div className="relative aspect-[3/4] bg-gradient-to-br from-slate-100 to-slate-50 p-4">
      <img
        src={catalog.image}
        alt={catalog.name}
        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
        <div className="p-4 text-white">
          <p className="text-sm font-medium">Click to view catalog</p>
        </div>
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-bold text-slate-800 mb-1">{catalog.name}</h3>
      <p className="text-sm text-slate-500 mb-3">{catalog.description}</p>
      <div className="flex flex-wrap gap-1">
        {catalog.industries.slice(0, 2).map((ind) => (
          <Badge key={ind} variant="secondary" className="text-xs bg-blue-50 text-blue-700">
            {ind}
          </Badge>
        ))}
        {catalog.industries.length > 2 && (
          <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-500">
            +{catalog.industries.length - 2} more
          </Badge>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Shared helpers for Filmop product display
// ---------------------------------------------------------------------------
const FILMOP_FALLBACK_IMG =
  "https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif";

const resolveFilmopImageUrl = (u) => {
  if (!u) return FILMOP_FALLBACK_IMG;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/"))
    return `https://www.filmopusa.com${u.replace("/products", "/images/product-page-images")}`;
  return FILMOP_FALLBACK_IMG;
};

const buildProductGallery = (product) => [
  resolveFilmopImageUrl(product.image_url),
  ...((product.additional_images || []).map(resolveFilmopImageUrl)),
];

const computeSoldInPricing = (product) => {
  const pack = product.case_pack || 1;
  const unitPrice = product.customer_price || 0;
  return {
    pack,
    unitPrice,
    soldInTotal: unitPrice * pack,
    soldInLabel: pack > 1 ? `Case of ${pack}` : (product.uom || "Each").toString(),
    unitLabel: (product.uom || "Each").toLowerCase(),
  };
};

// ---------------------------------------------------------------------------
// Shared Full-Screen Lightbox — used by both card and row layouts
// ---------------------------------------------------------------------------
const ProductLightbox = ({ product, open, onOpenChange, onAddToCart }) => {
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setActiveImageIdx(0);
      setQuantity(1);
    }
  }, [open]);

  const gallery = buildProductGallery(product);
  const activeImage = gallery[activeImageIdx] || gallery[0];
  const { pack, unitPrice, soldInTotal, soldInLabel, unitLabel } = computeSoldInPricing(product);

  const nextImage = (e) => {
    e?.stopPropagation();
    setActiveImageIdx((i) => (i + 1) % gallery.length);
  };
  const prevImage = (e) => {
    e?.stopPropagation();
    setActiveImageIdx((i) => (i - 1 + gallery.length) % gallery.length);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[100vw] w-screen h-screen sm:max-w-[100vw] p-0 border-0 rounded-none bg-slate-950/98 text-white flex flex-col"
        data-testid={`lightbox-${product.item_number}`}
      >
        <DialogHeader className="px-6 py-4 border-b border-white/10 flex flex-row items-center justify-between gap-4">
          <DialogTitle className="flex items-center gap-3 text-white">
            <Building2 className="w-5 h-5 text-cyan-400" />
            <span className="font-mono text-cyan-300 text-sm">{product.item_number}</span>
            <span className="hidden md:inline text-white/90 font-medium truncate max-w-[60vw]">
              {product.description}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          <div className="relative flex-1 flex items-center justify-center bg-white p-6 min-h-[50vh] lg:min-h-0">
            <img
              src={activeImage}
              alt={`${product.description} — image ${activeImageIdx + 1} of ${gallery.length}`}
              className="max-w-full max-h-full object-contain"
              data-testid="lightbox-main-image"
              onError={(e) => { e.target.src = FILMOP_FALLBACK_IMG; }}
            />
            {gallery.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-12 h-12 flex items-center justify-center transition-colors"
                  aria-label="Previous image"
                  data-testid="lightbox-prev"
                >
                  <ChevronRight className="w-6 h-6 rotate-180" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-12 h-12 flex items-center justify-center transition-colors"
                  aria-label="Next image"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <Badge className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white border-0 text-sm px-3 py-1">
                  {activeImageIdx + 1} / {gallery.length}
                </Badge>
              </>
            )}
          </div>

          <div className="lg:w-96 lg:border-l border-white/10 bg-slate-900 p-6 overflow-y-auto">
            <Badge className="bg-cyan-500 text-slate-900 mb-3 font-bold">Filmop USA</Badge>
            <h3 className="text-xl font-bold mb-2 leading-tight">{product.description}</h3>
            <p className="text-sm font-mono text-cyan-300 mb-4">{product.item_number}</p>

            <div className="space-y-2 text-sm mb-6">
              <div className="flex justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Unit of Measure</span>
                <span className="font-medium">{product.uom}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Case Pack</span>
                <span className="font-medium">{product.case_pack}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Category</span>
                <span className="font-medium text-right">{product.subcategory || product.category}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/10">
                <span className="text-white/60">Lead Time</span>
                <span className="font-medium">{product.lead_time_days || 3} days</span>
              </div>
            </div>

            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-4">
              <p className="text-xs text-cyan-200 font-semibold uppercase tracking-wide mb-1">Sold in {soldInLabel}</p>
              <p className="text-3xl font-bold text-cyan-300" data-testid={`lightbox-sold-in-total-${product.item_number}`}>
                ${soldInTotal.toFixed(2)}
              </p>
              {pack > 1 && (
                <p className="text-sm text-white/60 mt-1">(${unitPrice.toFixed(2)} per {unitLabel})</p>
              )}
            </div>

            <div className="flex gap-2 mb-4">
              <div className="flex items-center border border-white/20 rounded-lg bg-white/5">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 hover:bg-white/10 text-white"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-12 text-center bg-transparent border-0 focus:ring-0 text-sm text-white"
                  min="1"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-2 hover:bg-white/10 text-white"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <Button
                onClick={() => {
                  onAddToCart(product, quantity);
                  onOpenChange(false);
                }}
                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold"
                data-testid={`lightbox-add-to-cart-${product.item_number}`}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={() => window.open("https://www.filmopusa.com", "_blank")}
              className="w-full border-white/20 text-white hover:bg-white/10 hover:text-white"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View on filmopusa.com
            </Button>
          </div>
        </div>

        {gallery.length > 1 && (
          <div className="border-t border-white/10 bg-slate-900 px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-max">
              {gallery.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIdx(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === activeImageIdx
                      ? "border-cyan-400 ring-2 ring-cyan-400/40 scale-105"
                      : "border-white/20 hover:border-white/50 opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`lightbox-thumb-${idx}`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img src={url} alt="" className="w-full h-full object-contain bg-white" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Product Card Component
const ProductCard = ({ product, onAddToCart, cartQty }) => {
  const [quantity, setQuantity] = useState(1);
  const [showDetails, setShowDetails] = useState(false);

  const gallery = buildProductGallery(product);
  const primaryImage = gallery[0];
  const { pack, unitPrice, soldInTotal, soldInLabel, unitLabel } = computeSoldInPricing(product);

  const handleAdd = () => {
    onAddToCart(product, quantity);
    setQuantity(1);
  };

  return (
    <>
      <div
        data-testid={`product-${product.item_number}`}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all group flex flex-col"
      >
        {/* Product Image - BIG */}
        <div
          className="relative aspect-square bg-white p-2 cursor-pointer overflow-hidden"
          onClick={() => setShowDetails(true)}
          data-testid={`product-image-${product.item_number}`}
        >
          <img
            src={primaryImage}
            alt={product.description}
            loading="lazy"
            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
            onError={(e) => {
              e.target.src = "https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif";
            }}
          />
          {product.featured && (
            <div className="absolute top-2 left-2">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            </div>
          )}
          {cartQty > 0 && (
            <Badge className="absolute bottom-2 right-2 bg-[#003366]">
              {cartQty} in cart
            </Badge>
          )}
          {gallery.length > 1 && (
            <Badge className="absolute top-2 right-2 bg-white/90 text-slate-700 border border-slate-200">
              +{gallery.length - 1} photos
            </Badge>
          )}
          {/* Hover overlay hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100">
            <span className="text-white text-xs font-semibold bg-black/60 px-2 py-1 rounded">Click to enlarge</span>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4 flex flex-col flex-1">
          <p className="text-xs text-[#003366] font-semibold mb-1 flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            Filmop USA
          </p>
          <p className="text-xs font-mono text-slate-500 mb-1">{product.item_number}</p>
          <h3
            className="font-medium text-slate-800 text-sm leading-tight mb-2 line-clamp-2 cursor-pointer hover:text-[#003366]"
            onClick={() => setShowDetails(true)}
          >
            {product.description}
          </h3>

          {/* UOM */}
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
            <span className="bg-slate-100 px-2 py-0.5 rounded">{product.uom}</span>
            {product.case_pack > 1 && (
              <span className="bg-slate-100 px-2 py-0.5 rounded">Pack of {product.case_pack}</span>
            )}
          </div>

          {/* Pricing - Sold in (UOM) with total; unit price in parens */}
          <div className="mb-3 mt-auto">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Sold in {soldInLabel}</p>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-xl font-bold text-[#003366]" data-testid={`sold-in-total-${product.item_number}`}>
                ${soldInTotal.toFixed(2)}
              </span>
              {pack > 1 && (
                <span className="text-xs text-slate-500" data-testid={`unit-price-${product.item_number}`}>
                  (${unitPrice.toFixed(2)}/{unitLabel})
                </span>
              )}
            </div>
          </div>

          {/* Add to Cart */}
          <div className="flex gap-2">
            <div className="flex items-center border rounded-lg">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-2 hover:bg-slate-100"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-10 text-center border-0 focus:ring-0 text-sm"
                min="1"
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-2 hover:bg-slate-100"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <Button
              onClick={handleAdd}
              className="flex-1 bg-[#003366] hover:bg-[#002244]"
              data-testid={`add-to-cart-${product.item_number}`}
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>


      <ProductLightbox
        product={product}
        open={showDetails}
        onOpenChange={setShowDetails}
        onAddToCart={onAddToCart}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Compact List Row - for scanning hundreds of SKUs quickly
// ---------------------------------------------------------------------------
const ProductRow = ({ product, onAddToCart, cartQty }) => {
  const [quantity, setQuantity] = useState(1);
  const [showDetails, setShowDetails] = useState(false);

  const gallery = buildProductGallery(product);
  const primaryImage = gallery[0];
  const { pack, unitPrice, soldInTotal, soldInLabel, unitLabel } = computeSoldInPricing(product);

  const handleAdd = (e) => {
    e.stopPropagation();
    onAddToCart(product, quantity);
    setQuantity(1);
  };

  return (
    <>
      <div
        data-testid={`product-${product.item_number}`}
        onClick={() => setShowDetails(true)}
        className="bg-white border border-slate-200 rounded-lg hover:shadow-md hover:border-[#003366] transition-all cursor-pointer flex items-center gap-4 p-3 group"
      >
        {/* Thumbnail */}
        <div
          className="relative w-16 h-16 flex-shrink-0 bg-white rounded-md border border-slate-100 overflow-hidden"
          data-testid={`product-image-${product.item_number}`}
        >
          <img
            src={primaryImage}
            alt={product.description}
            loading="lazy"
            className="w-full h-full object-contain group-hover:scale-110 transition-transform"
            onError={(e) => { e.target.src = FILMOP_FALLBACK_IMG; }}
          />
          {product.featured && (
            <Star className="w-3 h-3 text-amber-500 fill-amber-500 absolute top-0.5 left-0.5" />
          )}
          {gallery.length > 1 && (
            <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[9px] px-1 rounded-tl leading-tight">
              +{gallery.length - 1}
            </span>
          )}
        </div>

        {/* Item # + Description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-mono text-slate-500">{product.item_number}</span>
            {cartQty > 0 && (
              <Badge className="bg-[#003366] text-[10px] px-1.5 py-0">{cartQty} in cart</Badge>
            )}
          </div>
          <h3 className="font-medium text-slate-800 text-sm leading-tight truncate group-hover:text-[#003366]">
            {product.description}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {product.subcategory || product.category}
          </p>
        </div>

        {/* UOM / Case Pack */}
        <div className="hidden md:flex flex-col items-center gap-1 text-xs w-24 flex-shrink-0">
          <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-600">{product.uom}</span>
          {pack > 1 && (
            <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-600">Case of {pack}</span>
          )}
        </div>

        {/* Pricing */}
        <div className="hidden sm:block text-right w-32 flex-shrink-0">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium leading-tight">
            Sold in {soldInLabel}
          </p>
          <p className="text-lg font-bold text-[#003366] leading-tight" data-testid={`row-sold-in-total-${product.item_number}`}>
            ${soldInTotal.toFixed(2)}
          </p>
          {pack > 1 && (
            <p className="text-[10px] text-slate-500 leading-tight">(${unitPrice.toFixed(2)}/{unitLabel})</p>
          )}
        </div>

        {/* Qty + Add */}
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <div className="hidden sm:flex items-center border rounded-md">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="p-1.5 hover:bg-slate-100"
              aria-label="Decrease quantity"
            >
              <Minus className="w-3 h-3" />
            </button>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-8 text-center border-0 focus:ring-0 text-xs p-0"
              min="1"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="p-1.5 hover:bg-slate-100"
              aria-label="Increase quantity"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          <Button
            size="sm"
            onClick={handleAdd}
            className="bg-[#003366] hover:bg-[#002244] h-8 px-3"
            data-testid={`add-to-cart-${product.item_number}`}
          >
            <ShoppingCart className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Add</span>
          </Button>
        </div>
      </div>

      <ProductLightbox
        product={product}
        open={showDetails}
        onOpenChange={setShowDetails}
        onAddToCart={onAddToCart}
      />
    </>
  );
};



// Main Filmop Store Component
export default function FilmopStorePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("catalogs"); // "catalogs" or "products"
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({ items: [], subtotal: 0, discount_total: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  
  // Get hospital ID from localStorage
  const hospitalId = localStorage.getItem("hospital_id") || "demo-hospital";

  // Fetch Filmop products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("category", "Janitorial & EVS");
      if (searchQuery) params.append("search", searchQuery);

      const res = await fetch(`${API}/marketplace/products?${params}&limit=1000`);
      const data = await res.json();
      
      // Filter to only Filmop products
      let filmopProducts = (data.products || []).filter(
        p => p.manufacturer?.toLowerCase().includes("filmop")
      );
      
      // Apply subcategory filter using predicate
      if (categoryFilter !== "all") {
        const cat = PRODUCT_CATEGORIES.find((c) => c.id === categoryFilter);
        if (cat) {
          filmopProducts = filmopProducts.filter((p) => cat.matches(p));
        }
      }
      
      setProducts(filmopProducts);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter]);

  // Fetch cart
  const fetchCart = useCallback(async () => {
    try {
      const res = await fetch(`${API}/marketplace/cart/${hospitalId}`);
      const data = await res.json();
      setCart(data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    }
  }, [hospitalId]);

  // Load data when switching to products tab
  useEffect(() => {
    if (activeTab === "products") {
      fetchProducts();
    }
    fetchCart();
  }, [activeTab, fetchProducts, fetchCart]);

  // Add to cart
  const handleAddToCart = async (product, quantity) => {
    try {
      const res = await fetch(`${API}/marketplace/cart/${hospitalId}/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id, quantity }),
      });
      const data = await res.json();
      setCart(data);
      toast.success(`Added ${quantity}x ${product.item_number} to cart`);
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  // Get cart quantity for a product
  const getCartQty = (productId) => {
    const item = cart.items?.find(i => i.product_id === productId);
    return item?.quantity || 0;
  };

  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <HospitalLayout>
      <div className="min-h-screen bg-slate-50" data-testid="filmop-store">
        <MarketplaceCategoryNav currentCategory="filmop" />
        <MarketplaceBreadcrumb trail={[{ label: "Filmop USA" }]} />
        {/* Filmop Brand Header */}
        <div className="bg-gradient-to-r from-[#003366] to-[#004488]">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Back & Cart */}
            <div className="flex items-center justify-between mb-4">
              <Link 
                to="/hospital/marketplace"
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to Marketplace</span>
              </Link>
              <Button
                onClick={() => navigate("/hospital/marketplace")}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Cart
                {cartItemCount > 0 && (
                  <Badge className="ml-2 bg-[#c9a227] text-[#003366]">
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Brand Logo & Info */}
            <div className="flex items-center gap-6">
              <div className="bg-white rounded-xl p-3">
                <img
                  src="https://www.filmopusa.com/images/Filmop-logo-Top-Website-50years.jpg"
                  alt="Filmop USA"
                  className="h-16 w-auto"
                />
              </div>
              <div className="text-white">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-[#c9a227] text-[#003366] font-bold">
                    Official Distributor
                  </Badge>
                  <Badge className="bg-white/20 text-white">
                    50 Years of Excellence
                  </Badge>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold">Filmop USA Store</h1>
                <p className="text-white/80 text-sm md:text-base">
                  Premium Janitorial Workstations & Microfiber Cleaning Solutions
                </p>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setActiveTab("catalogs")}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                  activeTab === "catalogs"
                    ? "bg-white text-[#003366]"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
                data-testid="tab-catalogs"
              >
                <BookOpen className="w-4 h-4 inline mr-2" />
                Catalogs
              </button>
              <button
                onClick={() => setActiveTab("products")}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                  activeTab === "products"
                    ? "bg-white text-[#003366]"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}
                data-testid="tab-products"
              >
                <Package className="w-4 h-4 inline mr-2" />
                Products
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* Catalogs Tab */}
          {activeTab === "catalogs" && (
            <>
              {/* Hero Section */}
              <div className="bg-gradient-to-br from-[#003366]/5 to-[#004488]/10 rounded-2xl p-8 mb-8 border border-[#003366]/10">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <Badge className="bg-[#c9a227] text-[#003366] mb-3">Industry Catalogs</Badge>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                      Professional Cleaning Solutions for Every Industry
                    </h2>
                    <p className="text-slate-600 mb-6">
                      Browse our comprehensive product catalogs designed for healthcare, education, 
                      hospitality, and facility management. Each catalog features industry-specific 
                      solutions backed by 50 years of Italian engineering excellence.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Award className="w-5 h-5 text-[#003366]" />
                        <span>50+ Years Experience</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span>Healthcare Certified</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Truck className="w-5 h-5 text-blue-600" />
                        <span>Fast Delivery</span>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <img
                      src="https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif"
                      alt="Filmop Workstations"
                      className="w-full rounded-xl shadow-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Catalog Grid */}
              <h3 className="text-xl font-bold text-slate-800 mb-4">Browse Catalogs by Industry</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                {FILMOP_CATALOGS.map((catalog) => (
                  <CatalogCard
                    key={catalog.id}
                    catalog={catalog}
                    onView={() => window.open(catalog.url, "_blank")}
                  />
                ))}
              </div>

              {/* Resources Section */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#003366]" />
                  Additional Resources
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <a
                    href="https://www.filmopusa.com/resources/resources-literature.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <FileText className="w-8 h-8 text-[#003366]" />
                    <div>
                      <p className="font-medium text-slate-800">Product Sheets</p>
                      <p className="text-sm text-slate-500">Technical specifications</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                  </a>
                  <a
                    href="https://www.filmopusa.com/resources/resources-videos.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <Package className="w-8 h-8 text-[#003366]" />
                    <div>
                      <p className="font-medium text-slate-800">Product Videos</p>
                      <p className="text-sm text-slate-500">See products in action</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                  </a>
                  <a
                    href="https://www.filmopusa.com/resources/resources-videos-assembly.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <Award className="w-8 h-8 text-[#003366]" />
                    <div>
                      <p className="font-medium text-slate-800">Assembly Videos</p>
                      <p className="text-sm text-slate-500">Setup guides</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                  </a>
                </div>
              </div>

              {/* Contact Banner */}
              <div className="mt-8 bg-[#003366] rounded-2xl p-6 text-white">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold mb-1">Need Help Choosing?</h3>
                    <p className="text-white/80 text-sm">
                      Our team can help you select the right cleaning solutions for your facility.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      onClick={() => window.location.href = "tel:2814563458"}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      (281) 456-3458
                    </Button>
                    <Button
                      className="bg-[#c9a227] hover:bg-[#b89220] text-[#003366]"
                      onClick={() => window.location.href = "mailto:orders@sterilemate.com"}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Us
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Products Tab */}
          {activeTab === "products" && (
            <>
              {/* Search & Filters */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search Filmop products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>

                  {/* View Toggle */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded-lg ${viewMode === "grid" ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-600"}`}
                      data-testid="view-mode-grid"
                      aria-label="Grid view"
                      title="Grid view"
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded-lg ${viewMode === "list" ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-600"}`}
                      data-testid="view-mode-list"
                      aria-label="Compact list view"
                      title="Compact list view (faster scanning)"
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Category Filters */}
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {PRODUCT_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategoryFilter(cat.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        categoryFilter === cat.id
                          ? "bg-[#003366] text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`filter-${cat.id}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Products Grid */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-8 h-8 animate-spin text-[#003366]" />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-600 mb-2">No products found</h3>
                  <p className="text-slate-500 mb-4">Try adjusting your search or filter criteria</p>
                  <Button onClick={() => { setSearchQuery(""); setCategoryFilter("all"); }}>
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-4">{products.length} Filmop products</p>
                  {viewMode === "grid" ? (
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {products.map((product) => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          onAddToCart={handleAddToCart}
                          cartQty={getCartQty(product.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2" data-testid="product-list-view">
                      {products.map((product) => (
                        <ProductRow
                          key={product.id}
                          product={product}
                          onAddToCart={handleAddToCart}
                          cartQty={getCartQty(product.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-[#003366] text-white py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <img
                  src="https://www.filmopusa.com/images/Filmop-logo-Top-Website-50years.jpg"
                  alt="Filmop USA"
                  className="h-12 bg-white rounded p-1 mb-4"
                />
                <p className="text-white/70 text-sm">
                  Filmop USA - 50 years of Italian engineering excellence in professional cleaning solutions.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-3">Sterile Mate Distribution</h4>
                <p className="text-white/70 text-sm">
                  396 W Greens Road, Suite 1139<br />
                  Houston, TX 77067<br />
                  (281) 456-3458<br />
                  orders@sterilemate.com
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-3">Quick Links</h4>
                <div className="space-y-2">
                  <a href="https://www.filmopusa.com" target="_blank" rel="noopener noreferrer" className="block text-white/70 hover:text-white text-sm">
                    Filmop USA Website
                  </a>
                  <Link to="/hospital/marketplace" className="block text-white/70 hover:text-white text-sm">
                    Back to Marketplace
                  </Link>
                  <Link to="/hospital/dashboard" className="block text-white/70 hover:text-white text-sm">
                    Hospital Dashboard
                  </Link>
                </div>
              </div>
            </div>
            <div className="border-t border-white/20 mt-8 pt-4 text-center text-white/50 text-sm">
              Sterile Mate LLC - Official Filmop USA Distributor
            </div>
          </div>
        </footer>
      </div>
    </HospitalLayout>
  );
}
